/**
 * Completion / summary screen.
 *
 * Renders after either:
 *   - A successful payment (status=CONFIRMED registration)
 *   - An approval-required submission (status=PENDING_APPROVAL)
 *
 * Surfaces:
 *   - Summary line per registration completed in this session (the
 *     "register another team?" loop appends to FlowContext.completedRegs).
 *   - "Register another team" CTA → resets per-team state, routes to
 *     /events/:eventId/teams.
 *   - "Back to {organizer}" CTA when EmbedClient.return_url is set
 *     (only in redirect mode — popup mode posts a message to host
 *     instead of rendering a return CTA).
 */
import { useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import type { ConfigResponse, EmbedClient } from '@/api/embed';
import { useFlow } from '@/state/FlowContext';
import { accountNotice, useAccountPromotion } from '@/state/useAccountPromotion';
import { useVocab } from '@/state/vocab';
import type { DisplayMode } from '@/App';
import '@/pages/DonePage.css';

interface Props {
  client: EmbedClient;
  config: ConfigResponse | null;
  displayMode: DisplayMode;
}

export function GfgDonePage({ client, config, displayMode }: Props) {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const awaitingApproval = params.get('awaiting_approval') === '1';
  const vocab = useVocab();

  const {
    registration,
    completedRegs,
    appendCompletedReg,
    resetForNewRegistration,
    createAccount,
  } = useFlow();


  // On first render, append the just-completed registration to the
  // session list. Guarded so re-renders don't double-append.
  useEffect(() => {
    if (registration && !completedRegs.find((r) => r.id === registration.id)) {
      appendCompletedReg(registration);
    }
  }, [registration, completedRegs, appendCompletedReg]);

  // "Save my team for next time" (checked on Confirm): create the
  // TourneyNation account now the registration is done, not on approval.
  const { status: accountStatus, message: accountMessage } = useAccountPromotion(
    client, !!registration && createAccount && !awaitingApproval,
  );

  // Popup mode: post completion to the host page so loader.js can
  // close the modal. Redirect mode: render the return CTA.
  useEffect(() => {
    if (displayMode === 'popup' && registration) {
      try {
        window.parent.postMessage(
          {
            type: 'tn:registration:complete',
            registration: {
              id: registration.id,
              status: registration.status,
              event_id: registration.event_id,
              team_id: registration.team_id,
            },
          },
          '*', // host origin will be locked down via referrer-policy /
              // CSP frame-ancestors; * here is for development. Production
              // build replaces this with the validated host origin.
        );
      } catch {
        // postMessage failure is non-fatal — host just won't auto-close.
      }
    }
  }, [displayMode, registration]);

  // Opened as a script popup window (the embed "popup window" integration —
  // e.g. the Wix button — uses window.open). There, "Back to organizer"
  // should CLOSE this popup, returning the visitor to the site they launched
  // from, rather than navigate the popup to the return URL.
  const isPopupWindow =
    typeof window !== 'undefined' && !!window.opener && window.opener !== window;

  return (
    <div className="tn-done">
      <h1 className="tn-done__title">
        {awaitingApproval ? 'Submitted for approval' : 'You\'re registered!'}
      </h1>
      <p className="tn-done__subtitle">
        {awaitingApproval
          ? `${config?.branding.organizer_name ?? 'The organizer'} will review your registration. We'll email you when they decide.`
          : 'A confirmation email is on its way.'}
      </p>

      {completedRegs.length > 0 ? (
        <div className="tn-card tn-done__summary">
          <div className="tn-done__summary-label">This session</div>
          {completedRegs.map((r) => (
            <div key={r.id} className="tn-done__summary-row">
              <span>{r.division ?? 'Registration'}</span>
              <span className={`tn-done__status tn-done__status--${r.status}`}>
                {r.status === 'pending_payment' ? 'Payment due' : r.status.replace(/_/g, ' ')}
              </span>
            </div>
          ))}
        </div>
      ) : null}

      {accountNotice(accountStatus, accountMessage) ? (
        <div className={`tn-done__account tn-done__account--${accountStatus}`}>
          {accountNotice(accountStatus, accountMessage)}
        </div>
      ) : null}

      <div className="tn-done__actions">
        {/* Charity/fundraiser registrants typically register once — the
            "register another" loop is a sports/club affordance (a coach
            entering multiple teams), so hide it for charity. */}
        {!vocab.isCharity ? (
          <button
            className="tn-btn tn-btn--secondary"
            type="button"
            onClick={() => {
              resetForNewRegistration();
              navigate(`/events/${eventId}/teams`);
            }}
          >
            Register another {vocab.entrant}
          </button>
        ) : null}
        {isPopupWindow ? (
          <button
            className="tn-btn tn-btn--primary"
            type="button"
            onClick={() => window.close()}
          >
            {config?.branding.organizer_name
              ? `Back to ${config.branding.organizer_name}`
              : 'Close'}
          </button>
        ) : displayMode === 'redirect' && config?.return_url ? (
          <a
            className="tn-btn tn-btn--primary"
            href={appendRegistrationToReturnUrl(config.return_url, registration?.id)}
          >
            Back to {config.branding.organizer_name}
          </a>
        ) : null}
      </div>
    </div>
  );
}

function appendRegistrationToReturnUrl(
  returnUrl: string,
  registrationId: string | undefined,
): string {
  if (!registrationId) return returnUrl;
  try {
    const url = new URL(returnUrl);
    url.searchParams.set('registration_id', registrationId);
    return url.toString();
  } catch {
    return returnUrl;
  }
}
